import {Card, CardBody, Col, Input, ListGroup, Row} from "reactstrap";
import {Search} from "react-feather";
import React, {useContext, useEffect, useRef, useState} from "react";
import axiosConfig from "../../../../Config/axios";
import Loader from "../../../../Layout/Loader";
import AuthorizationContext from "../../../../_helper/Authorization";
import PrivilegeListPagination from "../../../../CommonElements/Pagination/PrivilegeListPagination";
import {Permission, PermissionWithToggle} from "../Types/PermissionTypes";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";

interface PermissionListViewComponentProps {
    userID: number;
}

const PermissionListView: React.FC<PermissionListViewComponentProps> = ({userID}) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [permissions, setPermissions] = useState<PermissionWithToggle[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(12);
    const [totalRows, setTotalRows] = useState<number>(0);
    const searchTimeoutRef = useRef<number | null>(null);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    const normalizeSearchTerm = (value: string) => value.replace(/\s+/g, ' ').trim();

    useEffect(() => {
        if (searchTimeoutRef.current !== null) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = window.setTimeout(() => {
            setDebouncedSearchTerm(normalizeSearchTerm(searchTerm));
        }, 1000);
        return () => {
            if (searchTimeoutRef.current !== null) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, userID]);

    useEffect(() => {
        getPermissions(currentPage, perPage, debouncedSearchTerm);
    }, [currentPage, perPage, debouncedSearchTerm, userID]);

    const getPermissions = (page: number = currentPage, limit: number = perPage, name: string = debouncedSearchTerm) => {
        setLoaderVisibility(true);
        const searchName = normalizeSearchTerm(name);
        Promise.all([
            axiosConfig.get(`/privilege/permission`, {
                params: {
                    user_id: userID,
                    name: searchName || undefined,
                }
            }),
            axiosConfig.get(`/privilege/permission`, {
                params: {
                    name: searchName || undefined,
                    page,
                    per_page: limit,
                }
            })
        ])
            .then(([currentUserResponse, permissionResponse]) => {
                const userPermissionList = currentUserResponse?.data?.data?.data || [];

                if (permissionResponse?.data?.data?.data) {
                    setPermissionIsOn(permissionResponse.data.data.data, userPermissionList);
                }

                const meta = permissionResponse?.data?.data?.meta;
                if (meta) {
                    setCurrentPage(meta.current_page || page);
                    setPerPage(meta.per_page || limit);
                    setTotalRows(meta.total || 0);
                }
                setLoaderVisibility(false);
            })
            .catch(exception => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    const setPermissionIsOn = (permissionsList: Permission[], userPermissionsList: Permission[]) => {
        const permissionData = permissionsList.map(permission => ({
            ...permission,
            isOn: !!userPermissionsList.find((currentPermission) => currentPermission.id === permission.id)
        }));
        setPermissions(permissionData);
    }

    const handleToggle = (permissionId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        setLoaderVisibility(true);
        axiosConfig
            .get(`privilege/user/${permissionId}/permission`, {
                params: {
                    'user_id': userID,
                    'type': event?.target?.checked ? 'attach' : 'detach'
                }
            })
            .then(response => {
                if (response?.data?.data) {
                    setPermissionIsOn(permissions, response?.data?.data);
                }
                setLoaderVisibility(false);
            })
            .catch(exception => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    return (
        <>
            <Loader visibility={loaderVisibility}/>
            <Row>
                <CardBody>
                    <div className="job-filter">
                        <div className="faq-form">
                            <Input className="form-control" type="text" placeholder="Search.."
                                   value={searchTerm}
                                   onChange={(event) => setSearchTerm(event.target.value)}/>
                            <Search className="search-icon"/>
                            <div className="categories">
                                <ListGroup>
                                    <Row>
                                        {permissions.map((permission, key) =>
                                            <Col sm="4" key={key}>
                                                <Card>
                                                    <CardBody style={{height: '150px'}}
                                                              className="shadow-lg p-25 shadow-showcase text-center ">
                                                        <h6>
                                                            {permission.display_name}
                                                        </h6>
                                                        <label className="switch form-label">
                                                            <input type="checkbox"
                                                                   checked={permission.isOn || false}
                                                                   onChange={(event) => handleToggle(permission.id, event)}/>
                                                            <span className="switch-state"></span>
                                                        </label>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        )}
                                    </Row>
                                </ListGroup>
                                <PrivilegeListPagination
                                    currentPage={currentPage}
                                    perPage={perPage}
                                    totalRows={totalRows}
                                    visibleCount={permissions.length}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        </div>
                    </div>
                </CardBody>
            </Row>
        </>
    );

}
export default PermissionListView;